import React, { useState, useCallback, useEffect } from 'react';
import { path, propOr, pathOr, isEmpty, compose, prop, set } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_SETTINGS_INFO } from '../apollo/queries';
import {
  UPDATE_PROFILE,
  UPDATE_LOCATION,
  ADD_LOCATION,
  UPDATE_MEMBER,
  ADD_MEMBER,
  ADD_DISPOSITION_CODE,
  REMOVE_DISPOSITION_CODE,
  UPDATE_DISPOSITION_CODE,
  REMOVE_MEMBER,
  REMOVE_SHOP_LOCATION,
  CREATE_SHOP_SUBSCRIPTION,
  CANCEL_SHOP_SUBSCRIPTION,
  UPLOAD_PROFILE_LOGO,
  CREATE_CUSTOM_WEBHOOK,
  REMOVE_SHOP_CUSTOM_WEBHOOK,
  UPDATE_ONBOARDING_PROFILE,
  CREATE_DEPARTMENT,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
} from '../apollo/mutation';
import { Mutation } from 'react-apollo';
import Footer from './Footer';
import {
  Form,
  Page,
  Card,
  Layout,
  FormLayout,
  InlineError,
  Spinner,
  Heading,
  DescriptionList,
  TextField,
  AppProvider,
  Subheading,
  Select,
  SettingToggle,
  TextContainer,
  DropZone,
  Button,
  Frame,
  Modal,
  ResourceList,
  ResourceItem,
  TextStyle,
  Toast,
  ButtonGroup,
  ContextualSaveBar,
  Stack,
  Thumbnail,
  Caption,
  Icon,
  Tooltip,
  Pagination,
  EmptyState,
  Checkbox,
  Link,
} from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';
import Loader from 'react-loader-spinner';
import { DeleteMajorMonotone, SearchMajorMonotone, CirclePlusMinor } from '@shopify/polaris-icons';
import '@shopify/polaris/dist/styles.css';
import { defaultLoadImage } from '../../utils/defaultLoadImage';
import { validateEmail } from '../../utils/normalizer';
import { INDUSTRY_OPTIONS, EMPLOYEE_SIZE } from '../../utils/formDefaultValues';

const memberErrorsInitialState = {
  memberFirstName: false,
  memberLastName: false,
  memberEmail: false,
};

const locationErrorsInitialState = {
  locationName: false,
  city: false,
  statee: false,
  country: false,
  zipCode: false,
};

const webhookFormatOptions = [
  { label: 'Select a Format', value: '' },
  { label: 'JSON', value: 'json' },
  { label: 'XML', value: 'xml' },
];

const webhookEventOptions = [
  { label: 'Select an Event', value: '' },
  { label: 'New candidate applied', value: 'candidate_submission' },
  { label: 'List open jobs', value: 'open_jobs' },
  { label: 'List closed jobs', value: 'closed_jobs' },
  { label: 'Candidate moved to active', value: 'candidate_active' },
  { label: 'Candidate moved to inactive', value: 'candidate_inactive' },
  { label: 'Candidate hired', value: 'candidate_hired' },
];

const pageTitles = {
  general: 'General',
  notifications: 'Notifications',
  templates: 'Templates',
  'account-plan': 'Account plan',
  'hiring-team': 'Hiring team',
  'hiring-locations': 'Hiring locations',
};

const pageFooterTitles = {
  'hiring-team': 'Hiring team',
  'hiring-locations': 'Hiring locations',
};

const pageFooterLinks = {
  'hiring-team': 'https://help.shophire.co/article/27-hiring-team',
  'hiring-locations': 'https://help.shophire.co/article/9-hiring-locations',
};

const Settings = (props) => {
  const { app, shopOrigin, apiKey, match = {}, shopifyToken, history = {} } = { ...props };
  const { params = {} } = { ...match };
  const { category: settingsCategory = '' } = { ...params };

  const [isDirty, setIsDirty] = useState(false);
  const [weekly, setWeekly] = useState(false);
  const [daily, setDaily] = useState(false);
  const [branding, setBranding] = useState(false);
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(true);
  const [errors, setErrors] = useState('');
  const [updateLocation] = useMutation(UPDATE_LOCATION);
  const [addLocation] = useMutation(ADD_LOCATION);
  const [updateMember] = useMutation(UPDATE_MEMBER);
  const [deleteMember] = useMutation(REMOVE_MEMBER);

  const [createDepartment] = useMutation(CREATE_DEPARTMENT);
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT);
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT);

  const [addDispositionCode] = useMutation(ADD_DISPOSITION_CODE);
  const [updateDispositionCode] = useMutation(UPDATE_DISPOSITION_CODE);
  const [deleteDispositionCode] = useMutation(REMOVE_DISPOSITION_CODE);

  const [updateOnboardingProfile] = useMutation(UPDATE_ONBOARDING_PROFILE);
  const [addMember] = useMutation(ADD_MEMBER);
  const [cancelShopSubscription] = useMutation(CANCEL_SHOP_SUBSCRIPTION);
  const [deleteShopLocation] = useMutation(REMOVE_SHOP_LOCATION);
  const [deleteCustomWebhook] = useMutation(REMOVE_SHOP_CUSTOM_WEBHOOK);
  const [uploadProfileLogo] = useMutation(UPLOAD_PROFILE_LOGO);
  const [createCustomWebhook] = useMutation(CREATE_CUSTOM_WEBHOOK);
  const [createShopSubscription] = useMutation(CREATE_SHOP_SUBSCRIPTION, {
    onCompleted(data) {
      redirect.dispatch(Redirect.Action.REMOTE, data.createShopSubscription.confirmationUrl);
    },
  });

  const handleToggleWeekly = useCallback((value) => {
    setWeekly((weekly) => !weekly);
    value && setIsDirty(true);
  }, []);

  // const handleToggle = useCallback(() => setActive((active) => !active), []);

  const handleToggleBranding = useCallback((value) => {
    setBranding((branding) => !branding);
    value && setIsDirty(true);
  }, []);

  const handleReCaptchaToggle = useCallback((value) => {
    setRecaptchaEnabled((recaptchaEnabled) => !recaptchaEnabled);
    value && setIsDirty(true);
  }, []);

  const toggleIsDirty = useCallback(() => setIsDirty((isDirty) => !isDirty), []);

  const handleToggleDaily = useCallback(() => setDaily((daily) => !daily), []);

  const contentStatusDaily = daily ? 'Disable' : 'Enable';
  const contentStatusWeekly = weekly ? 'Disable' : 'Enable';
  const contentBranding = branding ? 'Disable' : 'Enable';
  const contentReCaptcha = recaptchaEnabled ? 'Disable' : 'Enable';

  const [active, setActive] = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [dispositionCodeModal, setDispositionCodeModal] = useState(false);
  const [createDispositionCodeModal, setCreateDispositionCodeModal] = useState(false);
  const [removeDispositionCodeModal, setRemoveDispositionCodeModal] = useState(false);

  const [departmentModal, setDepartmentModal] = useState(false);
  const [createDepartmentModal, setCreateDepartmentModal] = useState(false);
  const [removeDepartmentModal, setRemoveDepartmentModal] = useState(false);

  const [createLocationModal, setCreateLocationModal] = useState(false);
  const [removeLocationModal, setRemoveLocationModal] = useState(false);
  const [createMemberModal, setCreateMemberModal] = useState(false);
  const [removeMemberModal, setRemoveMemberModal] = useState(false);
  const [removeWebhookModal, setRemoveWebhookModal] = useState(false);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({});
  const [currentMember, setCurrentMember] = useState({});
  const [currentCode, setCurrentCode] = useState({});
  const [currentWebhook, setCurrentWebhook] = useState({});
  const [successToast, setSuccessToast] = useState(false);
  const [removeToast, setremoveToast] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [memberSearchValue, setMemberSearchValue] = useState('');
  const [locationsToShow, setLocationsToShow] = useState([]);
  const [membersToShow, setMembersToShow] = useState([]);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [locationsPage, setLocationsPage] = useState(1);
  const [membersPage, setMembersPage] = useState(1);
  const [file, setFile] = useState();
  const [logoImage, setLogoImage] = useState();
  const [memberErrors, setMemberErrors] = useState(memberErrorsInitialState);
  const [locationErrors, setLocationErrors] = useState(locationErrorsInitialState);

  const toggleIsWebhookModalOpen = () => setIsWebhookModalOpen(!isWebhookModalOpen);
  const toggleIsUpgradeModalOpen = () => setIsUpgradeModalOpen(!isUpgradeModalOpen);
  const handleDropZoneDrop = useCallback((_dropFiles, acceptedFiles, _rejectedFiles) => setFile((file) => acceptedFiles[0]), []);

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add Image' actionHint='or drop an file to upload' />;
  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size='small'
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
    </Stack>
  );

  const toggleActive = useCallback(() => setSuccessToast((successToast) => !successToast), []);
  const toggleActiveToast = useCallback(() => setSuccessToast((removeToast) => !removeToast), []);
  const handleModal = useCallback(() => setActive(!active), [active]);

  const handleLocationModal = useCallback(() => {
    setLocationErrors(locationErrorsInitialState);
    setLocationModal(!locationModal);
  }, [locationModal]);
  const handleCreateLocationModal = useCallback(() => {
    setLocationErrors(locationErrorsInitialState);
    setCreateLocationModal(!createLocationModal);
  }, [createLocationModal]);

  const handleRemoveLocationModal = useCallback(() => setRemoveLocationModal(!removeLocationModal), [removeLocationModal]);
  const handleRemoveWebhookModal = useCallback(() => setRemoveWebhookModal(!removeWebhookModal), [removeWebhookModal]);
  const handleMemberModal = useCallback(() => {
    setMemberErrors(memberErrorsInitialState);
    setMemberModal(!memberModal);
  }, [memberModal]);

  const handleCreateMemberModal = useCallback(() => {
    setMemberErrors(memberErrorsInitialState);
    setCreateMemberModal(!createMemberModal);
  }, [createMemberModal]);

  const handleRemoveMemberModal = useCallback(() => setRemoveMemberModal(!removeMemberModal), [removeMemberModal]);

  const handleDispositionCodeModal = useCallback(() => {
    setDispositionCodeModal(!dispositionCodeModal);
  }, [dispositionCodeModal]);

  const handleCreateDispositionCodeModal = useCallback(() => {
    setCreateDispositionCodeModal(!createDispositionCodeModal);
  }, [createDispositionCodeModal]);

  const handleRemoveDispositionCodeModal = useCallback(() => setRemoveDispositionCodeModal(!removeDispositionCodeModal), [
    removeDispositionCodeModal,
  ]);

  const handleDepartmentModal = useCallback(() => {
    setDepartmentModal(!departmentModal);
  }, [departmentModal]);

  const handleRemoveDepartmentModal = useCallback(() => setRemoveDepartmentModal(!removeDepartmentModal), [
    removeDepartmentModal,
  ]);

  const redirect = Redirect.create(app);

  const openRemoveLocationModal = (location) => {
    setCurrentLocation(location);
    setRemoveLocationModal(true);
  };

  const openRemoveMemberModal = (member) => {
    setCurrentMember(member);
    setRemoveMemberModal(true);
  };

  const openRemoveDispositionCodeModal = (dispositionCode) => {
    setCurrentCode(dispositionCode);
    setRemoveDispositionCodeModal(true);
  };

  const openRemoveDepartmentModal = (department) => {
    setSelectedDepartment(department);
    setRemoveDepartmentModal(true);
  };

  const openRemoveWebhookModal = (webhook) => {
    setCurrentWebhook(webhook);
    setRemoveWebhookModal(true);
  };

  const handleEditLocationModal = (location) => {
    if (location) {
      let { address1, address2, city, state, country, zipCode, id, name } = location;
      setState({
        ...form,
        address1: address1,
        address2: address2,
        city: city,
        statee: state,
        country: country,
        zipCode: zipCode,
        locationName: name,
        locationId: id,
      });
      setLocationModal(true);
    } else {
      setState({
        ...form,
        address1: '',
        address2: '',
        city: '',
        statee: '',
        country: '',
        zipCode: '',
        locationId: '',
        locationName: '',
      });
      setCreateLocationModal(true);
    }
  };

  const handleEditMemberModal = (member) => {
    if (!!member) {
      let { lastName: memberLastName, email: memberEmail, id: memberId, firstName: memberFirstName } = { ...member };
      setState({
        ...form,
        memberId,
        memberFirstName,
        memberLastName,
        memberEmail,
      });
      setMemberModal(true);
    } else {
      setState({
        ...form,
        memberId: '',
        memberFirstName: '',
        memberLastName: '',
        memberEmail: '',
      });
      setCreateMemberModal(true);
    }
  };

  const handleEditDispositionCodeModal = (dispositionCode) => {
    if (!!dispositionCode) {
      let { id: dispositionCodeId, name: dispositionCodeName } = { ...dispositionCode };
      setState({
        ...form,
        dispositionCodeId,
        dispositionCodeName,
      });
      setDispositionCodeModal(true);
    } else {
      setState({
        ...form,
        dispositionCodeId: '',
        dispositionCodeName: '',
      });
      setCreateDispositionCodeModal(true);
    }
  };

  const handleEditDepartmentModal = (department) => {
    if (!!department) {
      let { id: departmentId, headingText: departmentName } = { ...department };
      setState({
        ...form,
        departmentId,
        departmentName,
      });
      setDepartmentModal(true);
      setSelectedDepartment(department);
    } else {
      setState({
        ...form,
        departmentId: '',
        departmentName: '',
      });
      setDepartmentModal(true);
      setSelectedDepartment(null);
    }
  };

  const { loading, data, refetch } = useQuery(GET_SETTINGS_INFO, {
    variables: { shopifyToken },
  });

  const [form, setState] = useState({
    companyName: '',
    careerPageLink: `https://${shopOrigin}/a/careers/`,
    phoneNumber: '',
    contactEmail: '',
    address1: '',
    address2: '',
    departmentId: '',
    departmentName: '',
    googleIntegrationJsonKey: '',
    googleIntegrationEmail: '',
    city: '',
    country: '',
    googleIntegrationActive: true,
    statee: '',
    zipCode: '',
    locationId: '',
    locationName: '',
    industry: '',
    employeeSize: '',
    webhookEvent: 'candidate_submission',
    webhookFormat: 'json',
    webhookUrl: '',
    memberId: '',
    memberFirstName: '',
    memberLastName: '',
    memberEmail: '',
    dispositionCodeId: '',
    dispositionCodeName: '',
  });

  const shop = propOr({}, 'shop')(data);
  const profile = propOr({}, 'profile')(shop);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortal = shop?.careerPortal || {};
  const subscriptionPlan = currentSubscription?.subscriptionPlan;
  const jobLocations = shop?.locations || [];
  const jobLocationsLength = (shop?.locations || []).length;
  const allMembers = shop?.teamMembers || [];
  const allMembersLength = (shop?.teamMembers || []).length;
  const dispositionCodes = shop?.dispositionCodes || [];
  const departments = careerPortal?.departments || [];

  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPaidUser =
    isSubscribedUser && (currentSubscription.subscriptionType === 'Premium' || currentSubscription.subscriptionType === 'Growth');

  useEffect(() => {
    try {
      updateOnboardingProfile({ variables: { shopifyToken, onboardingInput: { company_profile_reviewed: true } } });
      refetch();
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    setState({
      webhookEvent: 'candidate_submission',
      webhookFormat: 'json',
      companyName: profile.companyName,
      phoneNumber: profile.phoneNumber,
      contactEmail: profile.contactEmail,
      industry: profile.industry,
      employeeSize: profile.employeeSize,
      careerPageLink: profile.careerPageLink,
    });

    setWeekly(profile.weeklyApplicantSummary);
    setBranding(profile.branding);
    setRecaptchaEnabled(profile.recaptchaEnabled);
  }, [loading]);

  useEffect(() => {
    if (profile.shopLogo) setLogoImage(profile.shopLogo);
  }, [profile.shopLogo]);

  useEffect(() => {
    const initialLocations = jobLocations.filter(({ name }) => name !== 'Custom');
    setCompanyLocations(initialLocations);
    setLocationsToShow(initialLocations.slice(0, 5));

    setTeamMembers(allMembers);
    setMembersToShow(allMembers.slice(0, 5));
  }, [jobLocationsLength, allMembersLength]);

  useEffect(() => {
    setTeamMembers(allMembers);
    setMembersToShow(allMembers.slice(0, 5));
  }, [allMembersLength]);

  const updateField = (name) => (value) => {
    setState({
      ...form,
      [name]: value,
    });
    setMemberErrors({
      ...memberErrors,
      [name]: false,
    });
    if (['locationName', 'city', 'statee', 'country', 'zipCode'].indexOf(name) !== -1) {
      setLocationErrors({
        ...locationErrors,
        [name]: false,
      });
    }
  };
  const toastMarkup = successToast ? <Toast content='You successfully updated your settings!' onDismiss={toggleActive} /> : null;

  // const toastRemoveMarkup = removeToast ? (
  //   <Toast content="You successfully remove your location!" onDismiss={toggleActiveToast} />
  // ) : null;

  const handleFormSubmit = (props) => {
    // setActive(!active);
  };

  const planStatus = isEmpty(currentSubscription) ? (
    <Card sectioned>
      <DescriptionList
        items={[
          {
            term: 'Current plan',
            description: 'Starter',
          },
          {
            term: 'Current status',
            description: 'Active',
          },
        ]}
      />
      <div className='left-hand-footer'>
        <ButtonGroup>
          <Button
            onClick={() => { history.push('/account-plan'); }}
            primary={true}>
            Upgrade plan
          </Button>
        </ButtonGroup>
      </div>
    </Card>
  ) : (
    <Card sectioned>
      <DescriptionList
        items={[
          {
            term: 'Current plan',
            description: `${currentSubscription.subscriptionType}`,
          },
          {
            term: 'Current status',
            description: 'Active',
          },
          {
            term: 'Trial ends on',
            description: `${currentSubscription.currentPeriodEnd ? currentSubscription.currentPeriodEnd : ''}`,
          },
        ]}
      />
      <div className='left-hand-footer'>
        <ButtonGroup>
          <Button onClick={() => history.push('/account-plan')}>Update plan</Button>
        </ButtonGroup>
      </div>
    </Card>
  );

  const reCapchtaContent = (
    <Layout.AnnotatedSection
      title='reCAPTCHA'
      description={<p>Enable Google reCAPTCHA to protect job applications from spam. Available on paid plans. May slightly reduce loading speed or cause occasional submission issues.</p>}>
      <SettingToggle
        action={{
          content: contentReCaptcha,
          onAction: handleReCaptchaToggle,
          disabled: !isPaidUser,
        }}
        enabled={recaptchaEnabled}>
        reCAPTCHA is <b>{recaptchaEnabled ? 'enabled' : 'disabled'}</b>
      </SettingToggle>
    </Layout.AnnotatedSection>
  );

  const brandingContent = (
    <Layout.AnnotatedSection
      title='ShopHire branding'
      description={<p>Paid account plans can remove ShopHire's branding from their career portal.</p>}>
      <SettingToggle
        action={{
          content: contentBranding,
          onAction: handleToggleBranding,
          disabled: !isPaidUser,
        }}
        enabled={branding}>
        Display ShopHire on career portal is <b>{branding ? 'enabled' : 'disabled'}</b>
      </SettingToggle>
    </Layout.AnnotatedSection>
  );

  const updateProfileFromModal = (props) => {
    let { updateProfile } = props;
    let brandingValue = branding;
    let recaptchaEnabledValue = recaptchaEnabled;
    let weeklyApplicantSummaryValue = weekly;
    // unsure where this is being null'ed
    if (brandingValue === null || brandingValue === undefined) {
      brandingValue = false;
    }

    if (recaptchaEnabledValue == null || recaptchaEnabledValue == undefined) {
      recaptchaEnabledValue = false;
    }

    if (weeklyApplicantSummaryValue === null || weeklyApplicantSummaryValue === undefined) {
      weeklyApplicantSummaryValue = false;
    }


    updateProfile({
      variables: {
        companyName: form.companyName,
        phoneNumber: form.phoneNumber,
        contactEmail: form.contactEmail,
        industry: form.industry,
        employeeSize: form.employeeSize,
        careerPageLink: form.careerPageLink,
        shopifyToken,
        branding: brandingValue,
        recaptchaEnabled: recaptchaEnabledValue,
        weeklyApplicantSummary: weeklyApplicantSummaryValue,
      },
    })
      .then((response) => {
        if (response.data.updateProfile.errors.length !== 0) {
          alert(response.data.updateProfile.errors);
          const { data } = response;
          console.log(data);
        } else {
          if (active) {
            setActive(!active);
          }
          refetch();
          setSuccessToast(true);
          const { data } = response;
          console.log(data);
        }
      })
      .catch((e) => {
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const createWebhookFromModal = (props) => {
    let { createCustomWebhook } = props;

    createCustomWebhook({
      variables: {
        url: form.webhookUrl,
        eventName: form.webhookEvent,
        format: form.webhookFormat,
        shopifyToken,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response.data.createCustomWebhook.errors.length !== 0) {
          alert(response.data.createCustomWebhook.errors);
          const { data } = response;
          console.log(data);
        } else {
          if (active) {
            setActive(!active);
          }
          toggleIsWebhookModalOpen();
          refetch();
          setSuccessToast(true);
          const { data } = response;
          console.log(data);
          setState({
            ...form,
            webhookUrl: '',
          });
        }
      })
      .catch((e) => {
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const contextualSaveBarMarkup = (updateProfile) =>
    isDirty ? (
      <ContextualSaveBar
        message='Unsaved changes'
        saveAction={{
          onAction: (e) => {
            e.preventDefault();
            updateProfileFromModal({ updateProfile });
            toggleIsDirty(false);
          },
        }}
        discardAction={{
          onAction: toggleIsDirty,
        }}
      />
    ) : null;

  const createSubscription = (subscriptionType) => () => {
    const response = createShopSubscription({
      variables: {
        shopifyToken,
        subscriptionType,
      },
    });

    const errors = pathOr([], ['data', 'createShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const cancelSubscription = () => {
    setLoading(true);

    const response = cancelShopSubscription({
      variables: {
        shopifyToken,
        subscriptionId: currentSubscription.id,
      },
    });

    setTimeout(() => {
      refetch();
      redirect.dispatch(Redirect.Action.ADMIN_PATH, '/apps/shophire/job-list');
      setLoading(false);
      setSuccessToast(true);
    }, 500);

    const errors = pathOr([], ['data', 'cancelShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const addLocationFromModal = () => {
    setLocationErrors(locationErrorsInitialState);
    const response = addLocation({
      variables: {
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        country: form.country,
        state: form.statee,
        zipCode: form.zipCode,
        name: form.locationName,
        shopifyToken: shopifyToken,
      },
    });

    setTimeout(() => {
      setCreateLocationModal(!createLocationModal);
      refetch();
      setSuccessToast(true);
    }, 500);

    const errors = pathOr([], ['data', 'addLocation', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const addTeamMemberFromModal = () => {
    setLoading(true);
    setMemberErrors(memberErrorsInitialState);
    const response = addMember({
      variables: {
        firstName: form.memberFirstName,
        lastName: form.memberLastName,
        email: form.memberEmail,
        shopifyToken: shopifyToken,
      },
    });

    const errors = pathOr([], ['data', 'addTeamMember', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      setLoading(false);
      setCreateMemberModal(!createMemberModal);
      refetch();
      setSuccessToast(true);
    }
  };

  const addDispositionCodeFromModal = () => {
    setLoading(true);
    const response = addDispositionCode({
      variables: {
        name: form.dispositionCodeName,
        shopifyToken: shopifyToken,
      },
    });

    const errors = pathOr([], ['data', 'addDispositionCode', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      setLoading(false);
      setCreateDispositionCodeModal(!createDispositionCodeModal);
      refetch();
      setSuccessToast(true);
    }
  };

  const removeLocationFromModal = (props) => {
    setLoading(true);

    const response = deleteShopLocation({
      variables: {
        locationId: currentLocation.id,
        shopifyToken: shopifyToken,
      },
    });

    setTimeout(() => {
      setLoading(false);
      setRemoveLocationModal(false);
      setSuccessToast(true);
      refetch();
    }, 1500);

    const errors = pathOr([], ['data', 'deleteShopLocation', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const removeTeamMemberFromModal = (props) => {
    setLoading(true);

    const response = deleteMember({
      variables: {
        memberId: currentMember.id,
        shopifyToken: shopifyToken,
      },
    });

    const errors = pathOr([], ['data', 'deleteTeamMember', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      setTimeout(() => {
        setLoading(false);
        setRemoveMemberModal(false);
        setSuccessToast(true);
        refetch();
      }, 1500);
    }
  };

  const removeDispositionCodeFromModal = (props) => {
    setLoading(true);

    const response = deleteDispositionCode({
      variables: {
        codeId: currentCode.id,
        shopifyToken: shopifyToken,
      },
    });

    const errors = pathOr([], ['data', 'deleteDispositionCode', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      setTimeout(() => {
        setLoading(false);
        setRemoveDispositionCodeModal(false);
        setSuccessToast(true);
        refetch();
      }, 1500);
    }
  };

  const removeWebhookFromModal = (props) => {
    setLoading(true);

    const response = deleteCustomWebhook({
      variables: {
        webhookId: currentWebhook,
        shopifyToken: shopifyToken,
      },
    });

    setTimeout(() => {
      setLoading(false);
      setRemoveWebhookModal(false);
      setSuccessToast(true);
      refetch();
    }, 1500);

    const errors = pathOr([], ['data', 'deleteCustomWebhook', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const onUploadLogo = async () => {
    setLoading(true);

    const response = await uploadProfileLogo({
      variables: {
        shopLogo: file,
        shopifyToken: shopifyToken,
      },
    });
    compose(setLogoImage, path(['data', 'uploadProfileLogo', 'profile', 'shopLogo']))(response);
    setLoading(false);

    const errors = pathOr([], ['data', 'deleteShopLocation', 'errors'])(response);
    const emptyErrors = isEmpty(errors);

    if (emptyErrors) {
      setSuccessToast(true);
      setFile();
    }
  };

  const onRemoveLogo = async () => {
    setLoading(true);

    const response = await uploadProfileLogo({
      variables: {
        shopifyToken: shopifyToken,
      },
    });
    compose(setLogoImage, path(['data', 'uploadProfileLogo', 'profile', 'shopLogo']))(response);
    setLoading(false);

    const errors = pathOr([], ['data', 'deleteShopLocation', 'errors'])(response);
    const emptyErrors = isEmpty(errors);

    if (emptyErrors) {
      setSuccessToast(true);
      setFile();
      refetch();
    }
  };
  const updateLocationFromModal = (props) => {
    setLocationErrors(locationErrorsInitialState);
    updateLocation({
      variables: {
        name: form.locationName,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.statee,
        country: form.country,
        zipCode: form.zipCode,
        locationId: form.locationId,
        shopifyToken,
      },
    })
      .then((response) => {
        if (response.data.updateLocation.errors.length !== 0) {
          alert(response.data.updateLocation.errors);
          const { data } = response;
          console.log(data);
        } else {
          setLocationModal(!locationModal);
          setSuccessToast(true);
          refetch();
          const { data } = response;
          console.log(data);
        }
      })
      .catch((e) => {
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const updateTeamMemberFromModal = (props) => {
    setLoading(true);
    setMemberErrors(memberErrorsInitialState);
    updateMember({
      variables: {
        firstName: form.memberFirstName,
        lastName: form.memberLastName,
        email: form.memberEmail,
        memberId: form.memberId,
        shopifyToken,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response.data.updateTeamMember.errors.length !== 0) {
          alert(response.data.updateTeamMember.errors);
        } else {
          setMemberModal(!memberModal);
          setSuccessToast(true);
          refetch();
          const { data } = response;
        }
      })
      .catch((e) => {
        setLoading(false);
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const updateDispositionCodeFromModal = (props) => {
    setLoading(true);
    updateDispositionCode({
      variables: {
        name: form.dispositionCodeName,
        id: form.dispositionCodeId,
        shopifyToken,
      },
    })
      .then((response) => {
        setLoading(false);
        if (response.data.updateDispositionCode.errors.length !== 0) {
          alert(response.data.updateDispositionCode.errors);
        } else {
          setDispositionCodeModal(!dispositionCodeModal);
          setSuccessToast(true);
          refetch();
          const { data } = response;
        }
      })
      .catch((e) => {
        setLoading(false);
        let messages = e.graphQLErrors[0].message;
        setErrors(messages);
      });
  };

  const modalLocationMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ updateLocation });
      }}>
      <FormLayout>
        <div>
          <TextField
            id='locationName'
            label='Name'
            value={form.locationName}
            name='locationName'
            onChange={updateField('locationName')}
          />
          {locationErrors.locationName && <InlineError message='This field is required' fieldID='locationName' />}
        </div>
        <TextField id='address1' label='Address' value={form.address1} name='address1' onChange={updateField('address1')} />
        <TextField
          id='address2'
          label='Apartment, suite, etc (Optional)'
          value={form.address2}
          name='address2'
          onChange={updateField('address2')}
        />
        <FormLayout.Group>
          <div>
            <TextField id='city' label='City' value={form.city} name='city' onChange={updateField('city')} />
            {locationErrors.city && <InlineError message='This field is required' fieldID='city' />}
          </div>
          <div>
            <TextField id='zipCode' label='Zip code' value={form.zipCode} name='zipCode' onChange={updateField('zipCode')} />
            {locationErrors.zipCode && <InlineError message='This field is required' fieldID='zipCode' />}
          </div>
        </FormLayout.Group>
        <FormLayout.Group>
          <div>
            <TextField id='country' label='Country' value={form.country} name='zipCode' onChange={updateField('country')} />
            {locationErrors.country && <InlineError message='This field is required' fieldID='country' />}
          </div>
          <div>
            <TextField id='state' label='State' value={form.statee} name='state' onChange={updateField('statee')} />
            {locationErrors.statee && <InlineError message='This field is required' fieldID='statee' />}
          </div>
        </FormLayout.Group>
      </FormLayout>
    </Form>
  );

  const modalMemberMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ updateMember });
      }}>
      <FormLayout>
        <FormLayout.Group>
          <div>
            <TextField
              id='memberFirstName'
              label='First name'
              value={form.memberFirstName}
              name='memberFirstName'
              onChange={updateField('memberFirstName')}
            />
            {memberErrors.memberFirstName && <InlineError message='This field is required' fieldID='memberFirstName' />}
          </div>
          <div>
            <TextField
              id='memberLastName'
              label='Last name'
              value={form.memberLastName}
              name='memberLastName'
              onChange={updateField('memberLastName')}
            />
            {memberErrors.memberLastName && <InlineError message='This field is required' fieldID='memberLastName' />}
          </div>
        </FormLayout.Group>
        <div>
          <TextField
            id='memberEmail'
            label='Email'
            value={form.memberEmail}
            name='memberEmail'
            onChange={updateField('memberEmail')}
          />
          {memberErrors.memberEmail && <InlineError message='This field is required' fieldID='memberEmail' />}
        </div>
      </FormLayout>
      <br />
    </Form>
  );

  const modalDispositionCodeMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ updateDispositionCode });
      }}>
      <FormLayout>
        <TextField
          id='dispositionCodeName'
          label='Disposition reason'
          value={form.dispositionCodeName}
          name='dispositionCodeName'
          onChange={updateField('dispositionCodeName')}
        />
        <br />
      </FormLayout>
    </Form>
  );

  const modalWebhookMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({ createCustomWebhook });
      }}>
      <FormLayout>
        <FormLayout.Group>
          <Select
            id='webhookEvent'
            name='webhookEvent'
            label='Event'
            options={webhookEventOptions}
            onChange={updateField('webhookEvent')}
            value={form.webhookEvent}
          />
          <Select
            id='webhookFormat'
            name='webhookFormat'
            label='Format'
            options={webhookFormatOptions}
            onChange={updateField('webhookFormat')}
            value={form.webhookFormat}
          />
        </FormLayout.Group>
        <TextField id='webhookUrl' label='URL' value={form.webhookUrl} name='webhookUrl' onChange={updateField('webhookUrl')} />
      </FormLayout>
    </Form>
  );

  const isPremiumPlan = currentSubscription.subscriptionType === 'Premium';
  const isGrowthPlan = currentSubscription.subscriptionType === 'Growth';
  const isStarterPlan = isEmpty(currentSubscription);

  const modalUpgradePlanMarkup = (
    <div className='plans-cards-container'>
      <div className={`plan-card ${isStarterPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Starter</h1>
        <h2 className='plan-card-subtitle'>Free</h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> 3 Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Google for Jobs
          </li>
        </ul>
        {!isStarterPlan && (
          <button
            type='button'
            className='plan-card-button'
            onClick={() => {
              cancelSubscription();
              refetch();
              toggleIsUpgradeModalOpen();
            }}>
            {isStarterPlan ? 'Get started' : 'Downgrade plan'}
          </button>
        )}
      </div>
      <div className={`plan-card ${isGrowthPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Growth</h1>
        <h2 className='plan-card-subtitle'>
          $29<span style={{ fontSize: '10px' }}>month</span>
        </h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> 5 Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Webhooks
          </li>
        </ul>
        {!isGrowthPlan && (
          <button
            onClick={() => {
              createSubscription('Growth')();
            }}
            type='button'
            className='plan-card-button'>
            {isStarterPlan ? 'Upgrade plan' : 'Downgrade plan'}
          </button>
        )}
      </div>
      <div className={`plan-card ${isPremiumPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Premium</h1>
        <h2 className='plan-card-subtitle'>
          $49<span style={{ fontSize: '10px' }}>month</span>
        </h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Unlimited Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Webhooks
          </li>
        </ul>
        {!isPremiumPlan && (
          <button
            onClick={() => {
              createSubscription('Premium')();
            }}
            type='button'
            className='plan-card-button'>
            {isStarterPlan || isGrowthPlan ? 'Upgrade plan' : 'Downgrade plan'}
          </button>
        )}
      </div>
    </div>
  );

  const removeModalMarkup = isLoading ? (
    <Spinner accessibilityLabel='Loading form field' size='large' />
  ) : (
    <TextContainer>
      <Heading>Are you sure you want to delete {currentLocation.name}?</Heading>
    </TextContainer>
  );

  const removeMemberModalMarkup = isLoading ? (
    <Spinner accessibilityLabel='Loading form field' size='large' />
  ) : (
    <TextContainer>
      <Heading>
        Are you sure you want to remove{' '}
        <strong>
          {currentMember.firstName} {currentMember.lastName}
        </strong>{' '}
        from team?
      </Heading>
    </TextContainer>
  );

  const removeDispositionCodeModalMarkup = isLoading ? (
    <Spinner accessibilityLabel='Loading form field' size='large' />
  ) : (
    <TextContainer>
      <Heading>Are you sure you want to remove selected disposition code?</Heading>
    </TextContainer>
  );

  const removeWebhookModalMarkup = isLoading ? (
    <Spinner accessibilityLabel='Loading form field' size='large' />
  ) : (
    <TextContainer>
      <Heading>Are you sure you want to delete this custom webhook?</Heading>
    </TextContainer>
  );

  const modalEditMarkup = (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit({});
      }}>
      <FormLayout>
        <TextField
          id='companyName'
          label='Company name'
          value={form.companyName}
          name='companyName'
          onChange={updateField('companyName')}
        />
        <TextField
          id='siteDomain'
          label='Site Domain'
          value={shopOrigin}
          name='siteDomain'
          type='text'
          readOnly={true}
          helpText={'Site domain should be updated from shopify settings page'}
        />
        <TextField
          id='sitemapLink'
          label='Career Portal Sitemap'
          value={shop.sitemapLink}
          name='sitemapLink'
          type='text'
          readOnly={true}
          helpText={'Sitemap link should be updated from shopify settings page'}
        />
        <TextField
          id='careerPageLink'
          label='Career Page Link'
          value={form.careerPageLink || `https://${shopOrigin}/a/careers/`}
          name='careerPageLink'
          type='text'
          onChange={updateField('careerPageLink')}
        />
        <TextField
          id='phoneNumber'
          label='Phone number'
          value={form.phoneNumber}
          name='phoneNumber'
          type='phone'
          onChange={updateField('phoneNumber')}
        />
        <TextField
          id='contactEmail'
          label='Contact email'
          value={form.contactEmail}
          name='contactEmail'
          type='email'
          onChange={updateField('contactEmail')}
        />
        <FormLayout.Group>
          <Select label='Industry' options={INDUSTRY_OPTIONS} onChange={updateField('industry')} value={form.industry} />
          <Select
            label='Number of employees'
            options={EMPLOYEE_SIZE}
            onChange={updateField('employeeSize')}
            value={form.employeeSize}
          />
        </FormLayout.Group>
      </FormLayout>
    </Form>
  );

  const validateMemberFields = () => {
    const updatedMemberErrors = {
      memberFirstName: compose(isEmpty, prop('memberFirstName'))(form),
      memberLastName: compose(isEmpty, prop('memberLastName'))(form),
      memberEmail: compose(isEmpty, prop('memberEmail'))(form),
    };
    setMemberErrors(updatedMemberErrors);
  };

  const validateLocationFields = () => {
    const updatedLocationErrors = {
      locationName: compose(isEmpty, prop('locationName'))(form),
      city: compose(isEmpty, prop('city'))(form),
      statee: compose(isEmpty, prop('statee'))(form),
      country: compose(isEmpty, prop('country'))(form),
      zipCode: compose(isEmpty, prop('zipCode'))(form),
    };
    setLocationErrors(updatedLocationErrors);
  };

  const demoImage = () => {
    if (!file && logoImage) return logoImage;
    if (file) return window.URL.createObjectURL(file);

    return defaultLoadImage;
  };

  if (!loading) {
    return (
      <Mutation mutation={UPDATE_PROFILE}>
        {(updateProfile) => (
          <AppProvider
            i18n={{
              Polaris: {
                ContextualSaveBar: {
                  save: 'Save',
                  discard: 'Discard',
                },
              },
            }}>
            <Frame>
              {contextualSaveBarMarkup(updateProfile)}
              <TitleBar title={pageTitles[settingsCategory]} breadcrumbs={[{ onAction: () => { history.push('/settings'); }, content: 'Settings' }]} />
              <Page title={pageTitles[settingsCategory]} breadcrumbs={[{ onAction: () => { history.push('/settings'); }, content: 'Settings' }]}>
                <Layout>
                  {settingsCategory === 'account-plan' && (
                    <Layout.AnnotatedSection
                      title='Account plan'
                      description={
                        <p style={{ marginBottom: '10px' }}>View account plan information and manage your subscription.</p>
                      }>
                      {planStatus}
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'general' && (
                    <Layout.AnnotatedSection
                      title='Profile'
                      description={
                        <p style={{ marginBottom: '10px' }}>
                          Shopify and your customers will use this information to contact you.
                        </p>
                      }>
                      <Card sectioned>
                        <DescriptionList
                          items={[
                            {
                              term: 'Company Name',
                              description: (shop.profile || {}).companyName,
                            },
                            {
                              term: 'Site Domain',
                              description: shopOrigin,
                            },
                            shop.sitemapLink && {
                              term: 'Career Portal Sitemap',
                              description: shop.sitemapLink,
                            },
                            {
                              term: 'Career Page Link',
                              description: (shop.profile || {}).careerPageLink || `https://${shopOrigin}/a/careers/`,
                            },
                            {
                              term: 'Phone Number',
                              description: (shop.profile || {}).phoneNumber || '---',
                            },
                            {
                              term: 'Email Address',
                              description: (shop.profile || {}).contactEmail,
                            },
                            (shop.profile || {}).industry && {
                              term: 'Industry',
                              description: shop.profile.industry,
                            },
                            (shop.profile || {}).employeeSize && {
                              term: 'Employee Size',
                              description: shop.profile.employeeSize,
                            },
                          ].filter((item) => !!item)}
                        />
                        <div className='left-hand-footer'>
                          <ButtonGroup>
                            <Button onClick={() => handleModal()}>Edit profile</Button>
                          </ButtonGroup>
                        </div>
                      </Card>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'general' && (
                    <Layout.AnnotatedSection
                      title='Google jobs logo display'
                      description='Indicate to Google what logo to display when your job posting appears in the search results'>
                      <Card sectioned>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '20px' }}>
                          <div style={{ overflow: 'hidden' }}>
                            <Subheading>Upload logo</Subheading>
                            <br />
                            <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                              {uploadedFile}
                              {fileUpload}
                            </DropZone>
                            <br />
                            <div className='Polaris-Layout__AnnotationDescription'>
                              <p>JPEG or PNG, max size of 2MB and square image files larger than 112 x 112 pixels.</p>
                            </div>
                          </div>
                          <div style={{ position: 'relative' }}>
                            <Subheading>Display demo</Subheading>
                            <br />
                            <Card>
                              <Card.Section>
                                <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gridGap: '15px' }}>
                                  <img style={{ width: '100%' }} src={demoImage()} />
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateRows: 'repeat(3, 1fr)',
                                      gridTemplateColumns: 'repeat(4, 1fr)',
                                      gridGap: '10px 0',
                                    }}>
                                    <span
                                      style={{ height: '10px', background: '#E2E3E7', borderRadius: '8px', gridColumn: '1 / 5' }}
                                    />
                                    <span
                                      style={{ height: '10px', background: '#E2E3E7', borderRadius: '8px', gridColumn: '1 / 5' }}
                                    />
                                    <span
                                      style={{ height: '10px', background: '#E2E3E7', borderRadius: '8px', gridColumn: '1 / 4' }}
                                    />
                                  </div>
                                </div>
                              </Card.Section>
                            </Card>
                            {!isLoading && file && (
                              <div style={{ position: 'absolute', bottom: '0', right: '0' }}>
                                <Button onClick={onUploadLogo} size='medium' primary={true}>
                                  Save
                                </Button>
                              </div>
                            )}
                            {!file && !!logoImage && (
                              <div style={{ position: 'absolute', bottom: '0', right: '0' }}>
                                <Button onClick={onRemoveLogo} size='medium' primary={true}>
                                  Remove image
                                </Button>
                              </div>
                            )}
                            {isLoading && (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '30px' }}>
                                <Loader type='TailSpin' color='#6371c7' height={40} width={40} />
                              </div>
                            )}
                          </div>
                        </div>
                      </Card>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'general' && (
                    <Layout.AnnotatedSection
                      title='Departments'
                      description={
                        <>
                          <p style={{ marginBottom: '5px' }}>Categorize your jobs by department.</p>
                          <Button plain external url={'https://help.shophire.co/article/46-departments'}>
                            Learn more about Departments
                          </Button>
                        </>
                      }>
                      <div className='DispositionCodes__list'>
                        <Card sectioned>
                          <ResourceList
                            items={departments}
                            renderItem={(item) => {
                              const { headingText, id } = item;
                              return (
                                <ResourceItem
                                  id={id}
                                  shortcutActions={[
                                    {
                                      content: 'Edit',
                                      onAction: () => handleEditDepartmentModal(item),
                                    },
                                    {
                                      content: 'Remove',
                                      onAction: () => openRemoveDepartmentModal(item),
                                    },
                                  ]}>
                                  <TextStyle variation='strong'>{headingText}</TextStyle>
                                </ResourceItem>
                              );
                            }}
                          />

                          <div className='left-hand-footer'>
                            <div></div>
                            <Button onClick={() => handleEditDepartmentModal(null)}>Add department</Button>
                          </div>
                        </Card>
                      </div>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'hiring-locations' && (
                    <Layout.AnnotatedSection
                      title='Hiring locations'
                      description={
                        <p style={{ marginBottom: '5px' }}>Manage the places your company will be hiring new employees.</p>
                      }>
                      <Card sectioned>
                        <div className='location-search-container'>
                          <Icon source={SearchMajorMonotone} />
                          <TextField
                            placeholder='Search locations'
                            value={searchValue}
                            onChange={(value) => {
                              setSearchValue(value);
                              if (value !== '') {
                                const updatedLocations = companyLocations
                                  .filter(({ name, city }) => {
                                    const lowerValue = value?.toLocaleLowerCase();
                                    const lowerName = name?.toLocaleLowerCase();
                                    const lowerCity = city?.toLocaleLowerCase();

                                    return lowerName?.includes(lowerValue) || lowerCity?.includes(lowerValue);
                                  })
                                  ?.slice();
                                setCompanyLocations(updatedLocations);
                                setLocationsToShow(updatedLocations.slice(0, 5));
                                setLocationsPage(1);
                              } else {
                                const initLocations = jobLocations.filter(({ name }) => name !== 'Custom');
                                setCompanyLocations(initLocations);
                                setLocationsToShow(initLocations.slice(0, 5));
                                setLocationsPage(1);
                              }
                            }}
                          />
                        </div>
                        <ResourceList
                          items={locationsToShow}
                          renderItem={(item) => {
                            const { address1, address2, city, id, name, state, zipCode, country } = item;
                            return (
                              <ResourceItem
                                id={id}
                                shortcutActions={[
                                  {
                                    content: 'Edit',
                                    onAction: () => handleEditLocationModal(item),
                                  },
                                  {
                                    content: 'Remove',
                                    onAction: () => openRemoveLocationModal(item),
                                  },
                                ]}>
                                <h3>
                                  <TextStyle variation='strong'>{name}</TextStyle>
                                </h3>
                                <div>{address1}</div>
                                <div>{address2}</div>
                                <div>
                                  {city}, {state}
                                </div>
                              </ResourceItem>
                            );
                          }}
                        />
                        <div className='location-footer'>
                          <div className='location-footer-btn'>
                            <Button onClick={() => handleEditLocationModal(null)}>Add location</Button>
                          </div>
                          <Pagination
                            hasPrevious={locationsPage > 1}
                            onPrevious={() => {
                              const prevPage = locationsPage - 1;
                              const upperLimit = 5 * prevPage;
                              const prevLocations = companyLocations.slice(upperLimit - 5, upperLimit);
                              setLocationsToShow(prevLocations);
                              setLocationsPage(prevPage);
                            }}
                            hasNext={locationsPage < Math.ceil(companyLocations.length / 5)}
                            onNext={() => {
                              const nextPage = locationsPage + 1;
                              const upperLimit = 5 * nextPage;
                              const nextLocations = companyLocations.slice(upperLimit - 5, upperLimit);
                              setLocationsToShow(nextLocations);
                              setLocationsPage(nextPage);
                            }}
                          />
                        </div>
                      </Card>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'hiring-team' && (
                    <Layout.AnnotatedSection
                      title='Hiring team'
                      description={
                        <p style={{ marginBottom: '5px' }}>
                          Add team members to your account, so they can receive notifications.
                        </p>
                      }>
                      <div className='TeamMembers__list'>
                        <Card sectioned>
                          <div className='location-search-container'>
                            <Icon source={SearchMajorMonotone} />
                            <TextField
                              placeholder='Search team members'
                              value={memberSearchValue}
                              onChange={(value) => {
                                setMemberSearchValue(value);
                                if (!!value) {
                                  const updatedMembers = teamMembers
                                    .filter(({ firstName, lastName, email }) => {
                                      const lowerValue = value?.toLocaleLowerCase();
                                      const lowerFirstName = firstName?.toLocaleLowerCase();
                                      const lowerLastName = lastName?.toLocaleLowerCase();
                                      const lowerEmail = email?.toLocaleLowerCase();

                                      return (
                                        lowerFirstName?.includes(lowerValue) ||
                                        lowerLastName?.includes(lowerValue) ||
                                        lowerEmail?.includes(lowerValue)
                                      );
                                    })
                                    ?.slice();
                                  setTeamMembers(updatedMembers);
                                  setMembersToShow(updatedMembers.slice(0, 5));
                                  setMembersPage(1);
                                } else {
                                  setTeamMembers(allMembers);
                                  setMembersToShow(allMembers.slice(0, 5));
                                  setMembersPage(1);
                                }
                              }}
                            />
                          </div>
                          {isEmpty(membersToShow) && (
                            <EmptyState
                              heading='No team members found'
                              action={{ content: 'Add team member', onAction: () => handleEditMemberModal(null) }}>
                              <p>Try using another search criteria or add a new team member</p>
                            </EmptyState>
                          )}
                          {!isEmpty(membersToShow) && (
                            <>
                              <br />
                              <ResourceList
                                items={membersToShow}
                                renderItem={(item) => {
                                  const { firstName, lastName, email, id } = item;
                                  return (
                                    <ResourceItem
                                      id={id}
                                      shortcutActions={[
                                        {
                                          content: 'Edit',
                                          onAction: () => handleEditMemberModal(item),
                                        },
                                        {
                                          content: 'Remove',
                                          onAction: () => openRemoveMemberModal(item),
                                        },
                                      ]}>
                                      <TextStyle variation='strong'>
                                        {firstName} {lastName}
                                      </TextStyle>
                                      <div>{email}</div>
                                    </ResourceItem>
                                  );
                                }}
                              />
                            </>
                          )}
                          <div className='location-footer'>
                            <div className='location-footer-btn'>
                              <Button onClick={() => handleEditMemberModal(null)}>Add team member</Button>
                            </div>
                            <Pagination
                              hasPrevious={membersPage > 1}
                              onPrevious={() => {
                                const prevPage = membersPage - 1;
                                const upperLimit = 5 * prevPage;
                                const prevMembers = teamMembers.slice(upperLimit - 5, upperLimit);
                                setMembersToShow(prevMembers);
                                setMembersPage(prevPage);
                              }}
                              hasNext={membersPage < Math.ceil(teamMembers.length / 5)}
                              onNext={() => {
                                const nextPage = membersPage + 1;
                                const upperLimit = 5 * nextPage;
                                const nextMembers = teamMembers.slice(upperLimit - 5, upperLimit);
                                setMembersToShow(nextMembers);
                                setMembersPage(nextPage);
                              }}
                            />
                          </div>
                        </Card>
                      </div>
                    </Layout.AnnotatedSection>
                  )}

                  {settingsCategory === 'general' && (
                    <Layout.AnnotatedSection
                      title='Disposition codes'
                      description={
                        <>
                          <p style={{ marginBottom: '5px' }}>Disposition codes track why candidates didn't work out.</p>
                          <Button plain external url={'https://help.shophire.co/article/40-what-are-disposition-codes'}>
                            Learn more about Disposition codes
                          </Button>
                        </>
                      }>
                      <div className='DispositionCodes__list'>
                        <Card sectioned>
                          <ResourceList
                            items={dispositionCodes}
                            renderItem={(item) => {
                              const { name, id } = item;
                              return (
                                <ResourceItem
                                  id={id}
                                  shortcutActions={[
                                    {
                                      content: 'Edit',
                                      onAction: () => handleEditDispositionCodeModal(item),
                                    },
                                    {
                                      content: 'Remove',
                                      onAction: () => openRemoveDispositionCodeModal(item),
                                    },
                                  ]}>
                                  <TextStyle variation='strong'>{name}</TextStyle>
                                </ResourceItem>
                              );
                            }}
                          />

                          <div className='left-hand-footer'>
                            <div></div>
                            <Button onClick={() => handleEditDispositionCodeModal(null)}>Add disposition code</Button>
                          </div>
                        </Card>
                      </div>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'templates' && (
                    <Layout.AnnotatedSection
                      title='Templates'
                      description={
                        <p style={{ marginBottom: '10px' }}>
                          Manage your company templates, for reusable items and notifications.
                        </p>
                      }>
                      <Card sectioned>
                        <DescriptionList
                          items={[
                            {
                              term: 'Email Templates',
                              description: (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button plain size={'slim'} onClick={() => history.push('/settings/templates/email')}>
                                    {'Manage'}
                                  </Button>
                                </div>
                              ),
                            },
                            {
                              term: 'Job Templates',
                              description: (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    plain
                                    size={'slim'}
                                    onClick={() => history.push('/settings/templates/job-descriptions')}>
                                    {'Manage'}
                                  </Button>
                                </div>
                              ),
                            },
                            {
                              term: 'Questionnaires',
                              description: (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button plain size={'slim'} onClick={() => history.push('/settings/templates/questionnaires')}>
                                    {'Manage'}
                                  </Button>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'notifications' && (
                    <Layout.AnnotatedSection
                      title='Notifications'
                      description={
                        <p style={{ marginBottom: '10px' }}>Manage the notifications sent to you and your hiring team.</p>
                      }>
                      <SettingToggle
                        action={{
                          content: contentStatusWeekly,
                          onAction: handleToggleWeekly,
                        }}
                        enabled={weekly}>
                        The weekly applicant summary email is <b>{weekly ? 'enabled' : 'disabled'}</b>.
                      </SettingToggle>
                    </Layout.AnnotatedSection>
                  )}
                  {settingsCategory === 'general' && reCapchtaContent}
                  {settingsCategory === 'general' && brandingContent}
                  {settingsCategory === 'notifications' && (
                    <Layout.AnnotatedSection
                      title='Webhooks'
                      description={
                        <>
                          <p>
                            You can subscribe to events for your candidates by creating webhooks that will push XML or JSON
                            notifications to a given URL.
                          </p>
                          <br />
                          {!subscriptionPlan?.weebhooks ? (
                            <Tooltip active={false} content='Please upgrade your account to use webhooks.'>
                              <Button disabled={!subscriptionPlan?.weebhooks} onClick={toggleIsWebhookModalOpen}>
                                Create webhook
                              </Button>
                            </Tooltip>
                          ) : (
                            <Button onClick={toggleIsWebhookModalOpen}>Create webhook</Button>
                          )}
                        </>
                      }>
                      <Card sectioned>
                        {!data?.shop?.customWebhooks?.length ? (
                          <p style={{ textAlign: 'center' }}>You haven't created any webhooks yet</p>
                        ) : (
                          <table style={{ width: '100%' }}>
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    textAlign: 'left',
                                    paddingBottom: '10px',
                                    borderBottom: '1px solid #F0F0F0',
                                    width: '240px',
                                  }}>
                                  Event
                                </th>
                                <th style={{ textAlign: 'left', paddingBottom: '10px', borderBottom: '1px solid #F0F0F0' }}>
                                  Callback URL
                                </th>
                                <th style={{ textAlign: 'left', paddingBottom: '10px', borderBottom: '1px solid #F0F0F0' }}>
                                  Format
                                </th>
                                <th style={{ textAlign: 'left', paddingBottom: '10px', borderBottom: '1px solid #F0F0F0' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {data?.shop?.customWebhooks.map(({ id, format, url, eventName }) => (
                                <tr key={id}>
                                  <td style={{ paddingTop: '10px', width: '240px' }}>
                                    {webhookEventOptions.find(({ value }) => value === eventName).label}
                                  </td>
                                  <td style={{ paddingTop: '10px' }}>{url}</td>
                                  <td style={{ paddingTop: '10px' }}>
                                    {webhookFormatOptions.find(({ value }) => value === format).label}
                                  </td>
                                  <td style={{ paddingTop: '10px' }}>
                                    <span className='remove-button-trash' style={{ cursor: 'pointer' }}>
                                      <Button onClick={() => openRemoveWebhookModal(id)} icon={DeleteMajorMonotone}>
                                        {' '}
                                      </Button>
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </Card>
                    </Layout.AnnotatedSection>
                  )}
                  {toastMarkup}
                </Layout>
              </Page>
              <Modal
                open={active}
                onClose={handleModal}
                title='Edit Profile'
                primaryAction={{
                  content: 'Update',
                  onAction: () => updateProfileFromModal({ updateProfile }),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleModal(),
                  },
                ]}
                loading={false}>
                <Modal.Section>{modalEditMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={removeLocationModal}
                onClose={handleRemoveLocationModal}
                title='Remove location'
                primaryAction={{
                  content: 'Remove',
                  onAction: () => removeLocationFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleRemoveLocationModal(),
                  },
                ]}
                loading={false}>
                <Modal.Section>{removeModalMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={removeMemberModal}
                onClose={handleRemoveMemberModal}
                title='Remove team member'
                primaryAction={{
                  content: 'Remove',
                  onAction: () => removeTeamMemberFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleRemoveMemberModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{removeMemberModalMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={removeDispositionCodeModal}
                onClose={handleRemoveDispositionCodeModal}
                title='Remove Disposition Code'
                primaryAction={{
                  content: 'Remove',
                  onAction: () => removeDispositionCodeFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleRemoveDispositionCodeModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{removeDispositionCodeModalMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={removeWebhookModal}
                onClose={handleRemoveWebhookModal}
                title='Remove Webhook'
                primaryAction={{
                  content: 'Remove',
                  onAction: () => removeWebhookFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleRemoveWebhookModal(),
                  },
                ]}
                loading={false}>
                <Modal.Section>{removeWebhookModalMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={locationModal}
                onClose={handleLocationModal}
                title='Edit location'
                primaryAction={{
                  content: 'Update',
                  onAction: () =>
                    isEmpty(form?.locationName) ||
                    isEmpty(form?.city) ||
                    isEmpty(form?.statee) ||
                    isEmpty(form?.country) ||
                    isEmpty(form?.zipCode)
                      ? validateLocationFields()
                      : updateLocationFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleLocationModal(),
                  },
                ]}
                loading={false}>
                <Modal.Section>{modalLocationMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={createLocationModal}
                onClose={handleCreateLocationModal}
                title='Add location'
                primaryAction={{
                  content: 'Save',
                  disabled:
                    isEmpty(form?.locationName) ||
                    isEmpty(form?.city) ||
                    isEmpty(form?.statee) ||
                    isEmpty(form?.country) ||
                    isEmpty(form?.zipCode),
                  onAction: () =>
                    isEmpty(form?.locationName) ||
                    isEmpty(form?.city) ||
                    isEmpty(form?.statee) ||
                    isEmpty(form?.country) ||
                    isEmpty(form?.zipCode)
                      ? validateLocationFields()
                      : addLocationFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleCreateLocationModal(),
                  },
                ]}
                loading={false}>
                <Modal.Section>{modalLocationMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={memberModal}
                onClose={handleMemberModal}
                title='Edit team member'
                primaryAction={{
                  content: 'Update',
                  onAction: () =>
                    isEmpty(form?.memberFirstName) || isEmpty(form?.memberLastName) || isEmpty(form?.memberEmail)
                      ? validateMemberFields()
                      : updateTeamMemberFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleMemberModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{modalMemberMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={createMemberModal}
                onClose={handleCreateMemberModal}
                title='Add team member'
                primaryAction={{
                  content: 'Save',
                  disabled:
                    isEmpty(form?.memberFirstName) ||
                    isEmpty(form?.memberLastName) ||
                    isEmpty(form?.memberEmail) ||
                    !validateEmail(form?.memberEmail),

                  onAction: () =>
                    isEmpty(form?.memberFirstName) || isEmpty(form?.memberLastName) || isEmpty(form?.memberEmail)
                      ? validateMemberFields()
                      : addTeamMemberFromModal(),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleCreateMemberModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{modalMemberMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={dispositionCodeModal}
                onClose={handleDispositionCodeModal}
                title='Edit Disposition Code'
                primaryAction={{
                  content: 'Update',
                  disabled: isEmpty(form?.dispositionCodeName),
                  onAction: () => (isEmpty(form?.dispositionCodeName) ? null : updateDispositionCodeFromModal()),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleDispositionCodeModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{modalDispositionCodeMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={createDispositionCodeModal}
                onClose={handleCreateDispositionCodeModal}
                title='Add Disposition Code'
                primaryAction={{
                  content: 'Create',
                  disabled: isEmpty(form?.dispositionCodeName),
                  onAction: () => (isEmpty(form?.dispositionCodeName) ? null : addDispositionCodeFromModal()),
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => handleCreateDispositionCodeModal(),
                  },
                ]}
                loading={loading}>
                <Modal.Section>{modalDispositionCodeMarkup}</Modal.Section>
              </Modal>

              <Modal
                open={isWebhookModalOpen}
                onClose={toggleIsWebhookModalOpen}
                title='Add a webhook'
                primaryAction={{
                  content: 'Save webhook',
                  onAction: () => {
                    setLoading(true);
                    createWebhookFromModal({ createCustomWebhook });
                  },
                  disabled: isLoading,
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: toggleIsWebhookModalOpen,
                  },
                ]}
                loading={false}>
                <Modal.Section>{modalWebhookMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={isUpgradeModalOpen}
                onClose={toggleIsUpgradeModalOpen}
                title='Pricing to suit all stores'
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: toggleIsUpgradeModalOpen,
                  },
                ]}
                loading={false}
                large>
                <Modal.Section>{modalUpgradePlanMarkup}</Modal.Section>
              </Modal>
              <Modal
                open={removeDepartmentModal}
                onClose={handleRemoveDepartmentModal}
                title='Remove department'
                primaryAction={{
                  content: 'Delete',
                  onAction: () => {
                    deleteDepartment({
                      variables: {
                        shopifyToken,
                        departmentId: selectedDepartment.id,
                      },
                    })
                      .then(() => {
                        handleRemoveDepartmentModal();
                        setSuccessToast(true);
                        setSelectedDepartment(null);
                        refetch();
                      })
                      .catch(() => {});
                  },
                  disabled: isLoading,
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: handleRemoveDepartmentModal,
                  },
                ]}
                loading={false}>
                <Modal.Section>
                  <p>Are you sure you want to delete this department?</p>
                </Modal.Section>
              </Modal>
              <Modal
                open={departmentModal}
                onClose={handleDepartmentModal}
                title={selectedDepartment?.id ? 'Update department' : 'Add department'}
                primaryAction={{
                  content: selectedDepartment?.id ? 'Update department' : 'Add department',
                  onAction: () => {
                    if (!selectedDepartment?.id) {
                      createDepartment({
                        variables: {
                          headingText: form.departmentName,
                          shopifyToken,
                        },
                      })
                        .then(() => {
                          setSuccessToast(true);
                          refetch();
                        })
                        .catch(() => {});
                    } else {
                      updateDepartment({
                        variables: {
                          departmentId: selectedDepartment.id,
                          headingText: form.departmentName,
                          subheadingText: selectedDepartment.subheadingText,
                          isHighlight: selectedDepartment.isHighlight || false,
                          shopifyToken,
                        },
                      })
                        .then(() => {
                          setSuccessToast(true);
                          refetch();
                        })
                        .catch(() => {});
                    }
                    handleDepartmentModal();
                    setSelectedDepartment(null);
                  },
                  disabled: !form.departmentName,
                }}
                secondaryActions={[
                  {
                    content: 'Cancel',
                    onAction: () => {
                      setState({
                        ...form,
                        departmentName: '',
                      });
                      setSelectedDepartment(null);
                      handleDepartmentModal();
                    },
                  },
                ]}
                loading={false}>
                <Modal.Section>
                  <FormLayout>
                    <TextField
                      id='departmentName'
                      label='Department name'
                      value={form.departmentName}
                      name='departmentName'
                      onChange={updateField('departmentName')}
                    />
                  </FormLayout>
                </Modal.Section>
              </Modal>
              <Footer
                link={pageFooterLinks[settingsCategory] || 'https://help.shophire.co/article/8-settings'}
                name={pageFooterTitles[settingsCategory] || 'Settings'}
              />
            </Frame>
          </AppProvider>
        )}
      </Mutation>
    );
  } else {
    return (
      <AppProvider>
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
        </div>
      </AppProvider>
    );
  }
};

export default Settings;
